import { useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'
import { useFormContext } from 'react-hook-form'
import { z } from 'zod'

import PageHeader from '../../../components/admin/PageHeader.js'
import ResourceTable, { type ColumnDef } from '../../../components/admin/ResourceTable.js'
import ResourceDrawer from '../../../components/admin/ResourceDrawer.js'
import BusinessHoursEditor, {
  DAYS,
  emptySchedule,
  type Schedule,
} from '../../../components/admin/conversation-engine/BusinessHoursEditor.js'
import { ZodForm, FormText, FormDropdown } from '../../../components/forms/index.js'
import { useAdminResource } from '../../../hooks/useAdminApi.js'

// ---------------------------------------------------------------------------
// Type — derived from apps/api/src/routes/conversations/admin/business-hours.ts
// + packages/conversations/src/schema/tenant-tables.ts (business_hours_configs)
// ---------------------------------------------------------------------------

export interface BusinessHours {
  id: string
  name: string
  timezone: string
  schedule: Schedule
  isActive: boolean
}

const DAY_ABBR = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function summarize(s: Schedule): string {
  const open = DAYS.filter((d) => s[d]?.enabled)
  if (open.length === 7) return '24/7'
  if (open.length === 0) return 'All days closed'
  const labels = open.map((d) => DAY_ABBR[DAYS.indexOf(d)])
  const first = s[open[0]!]!
  return `${labels.join(',')} ${first.open}-${first.close}`
}

// jsdom may not implement Intl.supportedValuesOf — guard with a static fallback.
function tzOptions(): Array<{ label: string; value: string }> {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] }
  const list =
    typeof intl.supportedValuesOf === 'function'
      ? intl.supportedValuesOf('timeZone')
      : [
          'UTC',
          'America/New_York',
          'America/Chicago',
          'America/Denver',
          'America/Los_Angeles',
          'Europe/London',
          'Europe/Berlin',
          'Asia/Kolkata',
          'Asia/Tokyo',
          'Australia/Sydney',
        ]
  return list.map((tz) => ({ label: tz, value: tz }))
}

function userTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

const BhSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  timezone: z.string().min(1, 'Timezone is required'),
  isActive: z.boolean(),
})

type BhFormValues = z.infer<typeof BhSchema>

function ScheduleField({
  value,
  onChange,
}: {
  value: Schedule
  onChange: (s: Schedule) => void
}): JSX.Element {
  return <BusinessHoursEditor value={value} onChange={onChange} />
}

function FormTimezone(): JSX.Element {
  return <FormDropdown name="timezone" label="Timezone" options={tzOptions()} filter />
}

function FormScheduleControl({
  schedule,
  setSchedule,
}: {
  schedule: Schedule
  setSchedule: (s: Schedule) => void
}): JSX.Element {
  // Schedule is held in page state (not in the Zod schema) since it's an
  // object grid; ZodForm still owns name/timezone/isActive. useFormContext
  // is referenced to keep this inside the form tree consistently.
  useFormContext()
  return (
    <div className="mb-3">
      <span className="mb-1 block text-sm font-medium">Weekly Schedule</span>
      <ScheduleField value={schedule} onChange={setSchedule} />
    </div>
  )
}

export default function BusinessHoursPage(): JSX.Element {
  const { items, loading, create, update, remove } = useAdminResource<BusinessHours>(
    '/admin/conversations/business-hours',
    { responseKey: 'rows' },
  )

  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<BusinessHours | null>(null)
  const [copySource, setCopySource] = useState<BusinessHours | null>(null)
  const [schedule, setSchedule] = useState<Schedule>(emptySchedule())
  const formRef = useRef<HTMLFormElement>(null)

  function openCreate(): void {
    setEditing(null)
    setCopySource(null)
    setSchedule(emptySchedule())
    setDrawerOpen(true)
  }

  function openEdit(row: BusinessHours): void {
    setEditing(row)
    setCopySource(null)
    setSchedule(row.schedule)
    setDrawerOpen(true)
  }

  function openCopy(row: BusinessHours): void {
    setEditing(null)
    setCopySource(row)
    setSchedule(row.schedule)
    setDrawerOpen(true)
  }

  function closeDrawer(): void {
    setDrawerOpen(false)
    setEditing(null)
    setCopySource(null)
  }

  async function handleSubmit(data: BhFormValues): Promise<void> {
    const payload = {
      name: data.name.trim(),
      timezone: data.timezone,
      schedule,
      isActive: data.isActive,
    }
    if (editing) await update(editing.id, payload)
    else await create(payload)
    closeDrawer()
  }

  function handleDelete(row: BusinessHours): void {
    confirmDialog({
      message: `Permanently delete "${row.name}"? This cannot be undone.`,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => void remove(row.id),
    })
  }

  const columns: ColumnDef<BusinessHours>[] = [
    { label: 'Name', accessor: 'name' },
    { label: 'Timezone', accessor: 'timezone' },
    { label: 'Schedule', accessor: (r) => summarize(r.schedule) },
    { label: 'Active', accessor: (r) => (r.isActive ? 'Yes' : 'No') },
  ]

  const defaultValues: BhFormValues = editing
    ? { name: editing.name, timezone: editing.timezone, isActive: editing.isActive }
    : copySource
      ? {
          name: `Copy of ${copySource.name}`,
          timezone: copySource.timezone,
          isActive: copySource.isActive,
        }
      : { name: '', timezone: userTimezone(), isActive: true }

  return (
    <div className="p-4">
      <PageHeader
        title="Business Hours"
        description="Define weekly operating schedules per timezone."
        actions={<Button label="+ New Business Hours" onClick={openCreate} />}
      />
      <ResourceTable<BusinessHours>
        columns={columns}
        rows={items}
        loading={loading}
        actions={['edit', 'copy', 'delete']}
        onEdit={openEdit}
        onCopy={openCopy}
        onDelete={handleDelete}
        empty={{
          title: 'No business hours configs',
          description: 'Create a config to define when your team is available.',
          action: <Button label="+ New Business Hours" onClick={openCreate} />,
        }}
      />
      <ResourceDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={async () => {}}
        formRef={formRef}
        width="wide"
        title={editing ? `Edit Business Hours: ${editing.name}` : copySource ? `Copy Business Hours: ${copySource.name}` : 'New Business Hours'}
      >
        <ZodForm schema={BhSchema} defaultValues={defaultValues} onSubmit={handleSubmit}>
          <FormText name="name" label="Name" required autoFocus />
          <FormTimezone />
          <FormScheduleControl schedule={schedule} setSchedule={setSchedule} />
          <button type="submit" className="rounded bg-[#16273F] px-4 py-2 text-white">
            Save
          </button>
        </ZodForm>
      </ResourceDrawer>
    </div>
  )
}
